import { ChangeDetectionStrategy, Component, OnInit, computed, signal } from '@angular/core';
import { AppSetupDb } from 'lib/cosmos/app-setup/appSetupDb.js';
import { numberHelper } from 'lib/helpers/number-helper.js';
import { IconType } from 'lib/ui/icons/index.js';
import { LayoutStyles } from 'lib/ui/layout/layout-ui.service.js';
import { IImage } from 'lib/ui/ui-models.js';
import { CoreAppComponent } from 'src/app/core/core-app.component.js';
import { match } from 'ts-pattern';
import { homeHelper } from './helpers/home-helper.js';

interface IFrequentlyAskedQuestion {
	readonly title: string;
	readonly text: string;
}

interface IFeature {
	readonly id: string;
	readonly isAlt: boolean;
	readonly title: string;
	readonly text: string;
	readonly icon: IconType;
	readonly image: IImage | undefined;
}

@Component({
	templateUrl: './home.component.html',
	selector: 'app-home',
	styleUrl: './home.component.scss',
	changeDetection: ChangeDetectionStrategy.OnPush,
	standalone: false
})
export class HomeComponent extends CoreAppComponent implements OnInit {
	protected readonly signupRouterLink = computed(() => {
		return this.dependencies.navigateService.signupPage().getUrl();
	});

	public readonly features = computed<readonly IFeature[]>(() => {
		const appSetup = this.appSetup();

		if (!appSetup) {
			return [];
		}

		const features: readonly AppSetupDb.IFeature[] = [
			appSetup.feature1,
			appSetup.feature2,
			appSetup.feature3,
			appSetup.feature4
		];

		return features.map<IFeature>((m, index) => {
			const isAlt = numberHelper.isOdd(index);

			return {
				title: m.title,
				isAlt: isAlt,
				text: m.text,
				icon: m.icon,
				id: m.title,
				image: match(this.screenSize())
					.returnType<IImage | undefined>()
					.with('Mobile', () => m.imageMobile?.[0]?.image)
					.otherwise(() => {
						if (isAlt) {
							return m.imageMobile?.[0]?.image;
						}
						return m.imageDesktop?.[0]?.image;
					})
			};
		});
	});

	private readonly selectedActiveFeature = signal<IFeature | undefined>(this.features()?.[0]);
	protected readonly activeFeature = computed<IFeature | undefined>(() => {
		const selectedActiveFeature = this.selectedActiveFeature();

		if (selectedActiveFeature) {
			return selectedActiveFeature;
		}

		const features = this.features();

		if (features.length) {
			return features[0];
		}

		return undefined;
	});

	public readonly anchors = homeHelper.homeAnchors;

	public readonly faqs: readonly IFrequentlyAskedQuestion[] = [
		{
			title: `Who is ${this.originalAppName()} for?`,
			text: `Personal trainers, gym coaches, fitness instructors — anyone who wants one easy platform to manage training, nutrition, and client communication.`
		},
		{
			title: `Can I use it on my phone?`,
			text: `Absolutely! ${this.originalAppName()} works on iOS and Android as a Progressive Web App (PWA).`
		},
		{
			title: `Can I cancel anytime?`,
			text: `Yes! Cancel whenever you want. You'll keep access until the end of your billing period, and you’ll never be charged anything extra.} until your subscription expires.`
		},
		{
			title: `Is there a refund policy?`,
			text: `Yes! We offer a 30-day money-back guarantee. Plus, the free trial lets you test everything before committing.`
		},
		{
			title: `Do clients have to pay?`,
			text: 'Nope — your clients use Gymvio for free.'
		}
	];

	public readonly getStartedUrl = computed<string>(() => {
		return this.dependencies.navigateService.signupPage().getUrl();
	});

	public readonly desktopTeaserImage = computed<IImage | undefined>(() => {
		return this.appSetup()?.mainTeaserDesktop?.[0]?.image;
	});

	public readonly mobileTeaserImage = computed<IImage | undefined>(() => {
		return this.appSetup()?.mainTeaserMobile?.[0]?.image;
	});

	public readonly layoutSizeStyles = computed<LayoutStyles>(() => {
		return this.dependencies.layoutUiService.getLayoutSizeStyles({
			screenService: this.dependencies.screenService,
			type: 'large'
		});
	});

	public readonly aiAssistantImage = computed<IImage | undefined>(() => {
		return this.appSetup()?.aiAssistantImage?.[0]?.image;
	});

	public readonly layoutFeaturesSizeStyles = computed<LayoutStyles>(() => {
		return this.dependencies.layoutUiService.getLayoutSizeStyles({
			screenService: this.dependencies.screenService,
			type: 'full'
		});
	});

	public readonly displayFeatures = signal<boolean>(true);

	public readonly showLoginInsteadOfHome = computed<boolean>(() => {
		return this.registeredSubdomain() ? true : false;
	});

	constructor() {
		super();
	}

	ngOnInit(): void {
		const authUser = this.authUser();
		if (authUser) {
			if (authUser.userType === 'client') {
				this.dependencies.navigateService.clientDashboard().navigate();
			} else if (authUser.userType === 'trainer') {
				this.dependencies.navigateService.trainerDashboard().navigate();
			}
		}
	}

	setActiveFeature(feature: IFeature): void {
		this.selectedActiveFeature.set(feature);
	}

	showPreviousFeature(): void {
		const allFeatures = this.features();
		const activeFeature = this.activeFeature();

		const indexOfActiveFeature = allFeatures.findIndex((m) => m.id === activeFeature?.id);

		let newIndex = indexOfActiveFeature - 1;

		if (newIndex < 0) {
			newIndex = allFeatures.length - 1;
		}

		this.setActiveFeature(allFeatures[newIndex]);
	}

	showNextFeature(): void {
		const allFeatures = this.features();
		const activeFeature = this.activeFeature();

		const indexOfActiveFeature = allFeatures.findIndex((m) => m.id === activeFeature?.id);

		let newIndex = indexOfActiveFeature + 1;

		if (newIndex >= allFeatures.length) {
			newIndex = 0;
		}

		this.setActiveFeature(allFeatures[newIndex]);
	}
}
